import React from 'react';

interface ProductVideoProps {
  src: string;
  poster: string;
  /** Intrinsic size, used only to reserve space so the page doesn't shift while loading. */
  width: number;
  height: number;
  /** Accessible description of what the recording shows. */
  label: string;
  caption: string;
}

/**
 * Looping, muted, inline screen recording for blog posts.
 *
 * The markup is prerendered, so it carries no `autoPlay` and loads nothing but the poster
 * (`preload="none"`). Playback starts on the client only once the video scrolls into view and
 * the reader has not asked for reduced motion; in every other case the poster stays put.
 */
export const ProductVideo: React.FC<ProductVideoProps> = ({
  src,
  poster,
  width,
  height,
  label,
  caption,
}) => {
  const ref = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motion.matches || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Autoplay can be refused (e.g. data saver); the poster simply remains.
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(video);

    // If the reader switches reduced motion on mid-visit, stop and return to the poster.
    const onMotionChange = () => {
      if (motion.matches) {
        observer.disconnect();
        video.pause();
        video.load();
      }
    };
    motion.addEventListener('change', onMotionChange);

    return () => {
      observer.disconnect();
      motion.removeEventListener('change', onMotionChange);
    };
  }, []);

  return (
    <figure className="my-6 mx-auto w-full max-w-[300px]">
      <video
        ref={ref}
        className="block w-full h-auto rounded-2xl border border-zinc-800 bg-zinc-900"
        style={{ aspectRatio: `${width} / ${height}` }}
        width={width}
        height={height}
        poster={poster}
        muted
        loop
        playsInline
        preload="none"
        aria-label={label}
      >
        <source src={src} type="video/mp4" />
      </video>
      <figcaption className="mt-3 text-center text-sm text-zinc-500">{caption}</figcaption>
    </figure>
  );
};
